import { useState } from "react";
import { useAuth } from "../../../lib/auth";
import { ConversationList } from "../components/ConversationList";
import { MessageThread } from "../components/MessageThread";
import { useContacts } from "../useContacts";
import type { SmsConversation } from "../types";

type Props = {
  phoneLine: "line1" | "line2" | null;
  refreshKey: number;
  onMessageSent: () => void;
  onContactAdded: () => void;
  contactRefreshKey: number;
};

export function MessagesView({
  phoneLine,
  refreshKey,
  onMessageSent,
  onContactAdded,
  contactRefreshKey,
}: Props) {
  const { me } = useAuth();
  const isAdmin = !!me?.is_admin;
  const { byNumber } = useContacts(contactRefreshKey);

  const [selectedContact, setSelectedContact] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<SmsConversation | null>(null);

  function handleSelect(conv: SmsConversation) {
    setSelectedContact(conv.contact_number);
    setSelectedConversation(conv);
  }

  const contactInfo = selectedContact ? byNumber[selectedContact] ?? null : null;

  return (
    <div className="flex h-full min-h-0">
      <div
        className={`w-full flex-shrink-0 overflow-y-auto border-r border-tribal-border bg-tribal-surface sm:w-80 lg:w-96 ${
          selectedContact ? "hidden sm:block" : ""
        }`}
      >
        <ConversationList
          phoneLine={phoneLine}
          selectedContact={selectedContact}
          onSelect={handleSelect}
          contactMap={byNumber}
          refreshKey={refreshKey}
        />
      </div>

      <div
        className={`flex min-w-0 flex-1 flex-col ${
          selectedContact ? "" : "hidden sm:flex"
        }`}
      >
        {selectedContact ? (
          <MessageThread
            contact={selectedContact}
            conversation={selectedConversation}
            contactInfo={contactInfo}
            isAdmin={isAdmin}
            onBack={() => {
              setSelectedContact(null);
              setSelectedConversation(null);
            }}
            onMessageSent={onMessageSent}
            onContactAdded={onContactAdded}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center text-tribal-muted">
            <div className="text-center">
              <div className="mb-3 text-5xl" aria-hidden>
                🪓
              </div>
              <p className="text-lg font-medium">Select a conversation</p>
              <p className="mt-1 text-sm">or hit Compose to start a new one.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
